import type { MNDrawingContent } from '../../../../../common/elements/elementModelTypes';
import type { Point } from '../../../../../common/maths/geometry/pointTypes';
import {
    PATH_TAG_REGEX,
    tryAffineTransformSvgPathData,
} from '../../../drawing/drawingEditor/utils/drawingSvgPathUtils';

export type DrawingTransform = {
    rotation: number;
    scaleX: number;
    scaleY: number;
};

export type DrawingTransformResult = {
    status: 'unchanged' | 'transformed' | 'unsupported';
    drawing: MNDrawingContent;
};

const PATH_DATA_ATTRIBUTE_REGEX = /\sd\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;

/** Creates absolute-point and direction-vector mappers for a centred drawing transform. */
const createCanvasPointMappers = ({
    width,
    height,
    transformedWidth,
    transformedHeight,
    rotation,
    scaleX,
    scaleY,
}: {
    width: number;
    height: number;
    transformedWidth: number;
    transformedHeight: number;
} & DrawingTransform) => {
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    /** Applies only the scale and rotation, suitable for relative path vectors. */
    const mapVector = ({ x, y }: Point): Point => {
        const scaledX = x * scaleX;
        const scaledY = y * scaleY;
        return {
            x: scaledX * cos - scaledY * sin,
            y: scaledX * sin + scaledY * cos,
        };
    };

    /** Applies the centred canvas transform to an absolute path point. */
    const mapAbsolute = ({ x, y }: Point): Point => {
        const mapped = mapVector({ x: x - width / 2, y: y - height / 2 });
        return {
            x: mapped.x + transformedWidth / 2,
            y: mapped.y + transformedHeight / 2,
        };
    };

    return { mapAbsolute, mapVector };
};

/** Bakes a drawing transform into every SVG path, reporting unsupported path data explicitly. */
const transformDrawingSvgPaths = ({
    svg,
    width,
    height,
    transformedWidth,
    transformedHeight,
    rotation,
    scaleX,
    scaleY,
}: {
    svg: string;
    width: number;
    height: number;
    transformedWidth: number;
    transformedHeight: number;
} & DrawingTransform): { svg: string; transformedAllPaths: boolean } => {
    const { mapAbsolute, mapVector } = createCanvasPointMappers({
        width,
        height,
        transformedWidth,
        transformedHeight,
        rotation,
        scaleX,
        scaleY,
    });
    const normalisedRotation = ((rotation % 360) + 360) % 360;
    const isQuarterTurn = normalisedRotation === 90 || normalisedRotation === 270;
    const isReflected = scaleX * scaleY < 0;
    let transformedPathCount = 0;
    let failedPathCount = 0;

    const transformedSvg = svg.replace(PATH_TAG_REGEX, (pathTag) =>
        pathTag.replace(PATH_DATA_ATTRIBUTE_REGEX, (attribute, quotedValue, doubleQuotedValue, singleQuotedValue) => {
            const pathData = doubleQuotedValue || singleQuotedValue || quotedValue;
            const quote = singleQuotedValue !== undefined ? "'" : '"';
            const transformedPathData = tryAffineTransformSvgPathData({
                pathData,
                mapAbsolute,
                mapVector,
                isReflected,
                isQuarterTurn,
                rotationDegrees: rotation,
            });

            if (!transformedPathData) {
                failedPathCount += 1;
                return attribute;
            }

            transformedPathCount += 1;
            return ` d=${quote}${transformedPathData}${quote}`;
        }),
    );

    return {
        svg: transformedSvg,
        transformedAllPaths: transformedPathCount > 0 && failedPathCount === 0,
    };
};

/** Bakes rotation and mirroring into drawing paths and reports whether every path was supported. */
export const getDrawingTransformResult = (
    drawing: MNDrawingContent,
    { rotation, scaleX, scaleY }: DrawingTransform,
): DrawingTransformResult => {
    const width = drawing.width ?? 0;
    const height = drawing.height ?? 0;
    if (!drawing.svg || !width || !height) return { status: 'unchanged', drawing };
    if (rotation === 0 && scaleX === 1 && scaleY === 1) return { status: 'unchanged', drawing };

    const normalisedRotation = ((rotation % 360) + 360) % 360;
    const isQuarterTurn = normalisedRotation === 90 || normalisedRotation === 270;
    const transformedWidth = isQuarterTurn ? height : width;
    const transformedHeight = isQuarterTurn ? width : height;
    const transformedDrawingSvg = transformDrawingSvgPaths({
        svg: drawing.svg,
        width,
        height,
        transformedWidth,
        transformedHeight,
        rotation,
        scaleX,
        scaleY,
    });
    if (!transformedDrawingSvg.transformedAllPaths) return { status: 'unsupported', drawing };

    const openTag = drawing.svg.match(/^<svg[^>]*>/)?.[0];
    const svg = openTag
        ? transformedDrawingSvg.svg.replace(
              openTag,
              openTag.replace(/viewBox="[^"]*"/, `viewBox="0 0 ${transformedWidth} ${transformedHeight}"`),
          )
        : transformedDrawingSvg.svg;

    return {
        status: 'transformed',
        drawing: {
            ...drawing,
            svg,
            width: transformedWidth,
            height: transformedHeight,
            paths: [],
        },
    };
};

/** Compatibility wrapper for callers that intentionally preserve unsupported drawing data unchanged. */
export const transformDrawingContent = (drawing: MNDrawingContent, transform: DrawingTransform): MNDrawingContent =>
    getDrawingTransformResult(drawing, transform).drawing;
